import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { SingleCheckOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { SwitchField } from '@connected/form-fields/components/switch';
import { Accordion } from '@connected/ui/components/accordion';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Input } from '@connected/ui/components/input';
import { Popout } from '@connected/ui/components/popout';
import { Separator } from '@connected/ui/components/separator';
import { Textarea } from '@connected/ui/components/textarea';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ElectricalInspectionKind } from '@/components/electrical-inspection-kind';
import { ElectricalProjectImage } from '@/components/electrical-project-image';
import { PinField } from '@/components/pin-field';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';

import type { ObservationObjectKinds, ObservationParentKinds, Remark } from '../utils/observation-types';

import { useStandardTexts } from '../hooks/use-standard-texts';
import { filterReportsByObjectAndParentKind } from '../utils/observation-utils';
import { ObservationAccordionItem } from './observation-accordion-item';
import { ObservationReportFields } from './observation-report-fields';

export type ObservationModalContentProps = {
    type: 'create' | 'edit';
    electricalProjectVersionId: string;
    objectKind: ObservationObjectKinds;
    parentKind: ObservationParentKinds;
    form: FormApi<Remark, ZodValidator> & ReactFormApi<Remark, ZodValidator>;
    onSubmit: () => void;
    onClose: () => void;
    disabled?: boolean;
};

export function ObservationModalContent(props: ObservationModalContentProps) {
    const { type, electricalProjectVersionId, form, onSubmit, onClose, disabled = false, objectKind, parentKind } = props;

    const { t } = useTranslation('re-inspections');

    const { context } = useElectricalProject();

    const { data: classificationData } = useQuery(
        elektraAppQueryOptions.project.inspection.observations.classificationGetOptions({
            electricalProjectVersionId,
        }),
    );

    const texts = useStandardTexts({
        electricalProjectVersionId,
        parentKind,
    });

    const recommendations = texts?.Items?.flatMap(item => item.Recommendations?.map(recommendation => ({
        Text: recommendation.Description!,
    })) || []) || [];

    const remarks = texts?.Items?.flatMap(item => item.Remarks?.map(remark => ({
        Text: remark.Description!,
    })) || []) || [];

    const remarkOptions = remarks.map(item => ({
        label: item.Text!,
        value: item.Text!,
    })) || [];

    const recommendationOptions = recommendations.map(item => ({
        label: item.Text!,
        value: item.Text!,
    })) || [];

    function handlePinChange(pins: { x: number | null; y: number | null }) {
        console.warn('Pin change:', pins);
        form.setFieldValue('floorplanPinCoordinateX', pins.x);
        form.setFieldValue('floorplanPinCoordinateY', pins.y);
        console.warn('Form state after pin change:', form.state.values);
    }

    function handleFloorplanChange(fileId: string | null) {
        console.warn('Floorplan change:', fileId);
        form.setFieldValue('floorplanElectricalProjectFileId', fileId);
        handlePinChange({ x: null, y: null }); // Reset pins when changing image
        console.warn('Form state after floorplan change:', form.state.values);
    }

    const electricalProjectFileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/observation',
        mutationOptions: {
            onMutate: async ({ ElectricalProjectFileId }) => {
                const previousFiles = form.state.values.electricalProjectFiles;

                const newFiles = [...previousFiles, ElectricalProjectFileId!];

                form.setFieldValue('electricalProjectFiles', newFiles);
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    });

    const { data } = useQuery(elektraAppQueryOptions.project.files.listOptions({ electricalProjectVersionId }));

    const filteredReports = useMemo(() => filterReportsByObjectAndParentKind(form.state.values.reports, objectKind, parentKind), [form.state.values.reports, objectKind, parentKind]);

    // Get the original index of the first filtered report in the reports array
    const firstFilteredReportIndex = useMemo(() => {
        if (filteredReports.length === 0)
            return 0;
        const firstFilteredReport = filteredReports[0];
        return form.state.values.reports.findIndex(
            report => report.inspectionKind === firstFilteredReport.inspectionKind,
        );
    }, [filteredReports, form.state.values.reports]);

    return (
        <Popout.Content aria-describedby="observation-content-description" className="max-w-3xl max-h-[90vh]">
            <Popout.Header>
                <Popout.Title>{type === 'create' ? t('installation.distribution-box.modal.title.label-create') : t('installation.distribution-box.modal.title.label-edit')}</Popout.Title>
                <Popout.Description className="sr-only">{type === 'create' ? t('installation.distribution-box.modal.description.label-create') : t('installation.distribution-box.modal.description.label-edit')}</Popout.Description>
            </Popout.Header>
            <Popout.Body className="gap-6 py-6">
                <div className="flex flex-col gap-4">
                    <form.Field
                        name="electricalProjectFiles"
                        children={() => (
                            <div className="flex flex-col gap-2">
                                <p className="text-sm font-medium">Foto's</p>
                                <FileInput
                                    onChange={(fileList) => {
                                        const file = fileList[0];

                                        if (!file)
                                            return;

                                        electricalProjectFileUpload.upload({ file });
                                    }}
                                    accept={electricalProjectFileUpload.accept ?? undefined}
                                    disabled={electricalProjectFileUpload.isLoading}
                                />

                                <form.Subscribe
                                    selector={state => state.values.electricalProjectFiles}
                                >
                                    {electricalProjectFiles => (
                                        <div className="flex flex-col gap-2">
                                            {electricalProjectFiles.map((electricalProjectFileId) => {
                                                return (
                                                    <div key={electricalProjectFileId} className="relative flex items-center gap-2">
                                                        <ElectricalProjectImage
                                                            className="h-[112px] w-full rounded-lg"
                                                            query={{ ElectricalProjectFileId: electricalProjectFileId }}
                                                            expandable
                                                        />
                                                        <Button
                                                            className="absolute top-1 right-1 bg-background/50"
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => {
                                                                const previousFiles = form.state.values.electricalProjectFiles;

                                                                const newFiles = previousFiles?.filter(fileId => fileId !== electricalProjectFileId);

                                                                form.setFieldValue('electricalProjectFiles', newFiles);
                                                            }}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </form.Subscribe>
                                <p className="text-sm">U kunt meerdere foto's uploaden</p>
                            </div>
                        )}
                    />

                    {/* Remark Fields */}
                    <form.Field
                        name="remark"
                        validators={{
                            onChange: validators.patchElectricalProjectObservationCommand.remark,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label label={t('observations.form.selectDefaultRemark')} className="text-sm font-medium" htmlFor="">
                                <SingleOptionField
                                    name={name}
                                    handleChange={handleChange}
                                    state={state}
                                    options={remarkOptions}
                                    render={SingleCheckOptionItem}
                                    dropdown
                                    closeOnClick
                                    emptyOptionsMessage="Geen standaard opmerkingen"
                                    search
                                    placeholder="Zoek opmerking"
                                    disabled={disabled}
                                />
                            </Label>
                        )}
                    </form.Field>

                    <form.Field
                        name="remark"
                        validators={{
                            onChange: validators.patchElectricalProjectObservationCommand.remark,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label label={t('observations.form.remark')} className="text-sm font-medium">
                                <Textarea
                                    name={name}
                                    onChange={e => handleChange(e.target.value)}
                                    value={state.value || ''}
                                    placeholder="Noteer opmerking"
                                    disabled={disabled}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </form.Field>

                    {/* Recommendation Fields */}
                    {context.enabledElectricalProjectRecommendations && (
                        <>
                            <form.Field
                                name="recommendation"
                                validators={{
                                    onChange: validators.patchElectricalProjectObservationCommand.recommendation,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('observations.form.selectDefaultRecommendation')} className="text-sm font-medium" htmlFor="">
                                        <SingleOptionField
                                            name={name}
                                            handleChange={handleChange}
                                            state={state}
                                            options={recommendationOptions}
                                            render={SingleCheckOptionItem}
                                            dropdown
                                            closeOnClick
                                            emptyOptionsMessage="Geen standaard aanbevelingen"
                                            search
                                            placeholder="Zoek aanbeveling"
                                            disabled={disabled}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>
                            <form.Field
                                name="recommendation"
                                validators={{
                                    onChange: validators.patchElectricalProjectObservationCommand.recommendation,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label label={t('observations.form.recommendation')} className="text-sm font-medium">
                                        <Textarea
                                            name={name}
                                            onChange={e => handleChange(e.target.value)}
                                            value={state.value || ''}
                                            disabled={disabled}
                                            placeholder="Noteer aanbeveling"
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </form.Field>
                        </>
                    )}
                    <Separator />
                </div>

                {filteredReports.length === 0 && <p className="text-sm text-text-tertiary">Kan geen constatering aanmaken, selecteer eerst een inspectierapport.</p>}
                {filteredReports.length === 1 && filteredReports[0].enabled
                    ? (
                            <>
                                {/* Use ObservationReportFields for all classification fields in single classification */}
                                <ObservationReportFields
                                    form={form}
                                    reportIndex={firstFilteredReportIndex}
                                    inspectionKind={filteredReports[0].inspectionKind!}
                                    classificationData={classificationData}
                                    disabled={disabled}
                                    isSingleClassification={true}
                                />
                            </>
                        )
                    : (
                            <div className="flex flex-col gap-4">
                                <h2 className="text-sm text-text-tertiary">Selecteer per scope of dit van toepassing is.</h2>
                                <form.Subscribe
                                    selector={(state) => {
                                        const reports = state.values.reports;
                                        const inspectionKinds = reports.map(report => report.inspectionKind);

                                        return { inspectionKinds, reports };
                                    }}
                                >
                                    {({ reports }) => (
                                        <Accordion type="multiple" value={reports?.filter(report => report.enabled).map(report => report.inspectionKind)}>
                                            {reports?.map((report, index) => {
                                                return (
                                                    <Fragment key={report.inspectionKind}>
                                                        <form.Field name={`reports[${index}].enabled`}>
                                                            {({ handleChange, state }) => (
                                                                <SwitchField
                                                                    name={`reports[${index}].enabled`}
                                                                    className="p-0"
                                                                    value={state.value ?? false}
                                                                    label={<ElectricalInspectionKind inspectionKind={report.inspectionKind} />}
                                                                    handleChange={(value) => {
                                                                        // TODO: Remove this once we have a way to handle this
                                                                        // if (value === true) {
                                                                        //     const previousEnabledReport = reports.find(report => report.enabled);
                                                                        //     if (previousEnabledReport && previousEnabledReport.inspectionKind !== report.inspectionKind) {
                                                                        //         form.setFieldValue(`reports[${index}]` as any, {
                                                                        //             ...report,
                                                                        //             enabled: true,
                                                                        //             electricalObservationClassificationCodeId: previousEnabledReport.electricalObservationClassificationCodeId,
                                                                        //             electricalObservationClassificationGroupId: previousEnabledReport.electricalObservationClassificationGroupId,
                                                                        //         } satisfies ObservationReport);
                                                                        //         return;
                                                                        //     }
                                                                        // }
                                                                        handleChange(value);
                                                                    }}
                                                                    disabled={disabled}
                                                                />
                                                            )}
                                                        </form.Field>
                                                        <ObservationAccordionItem
                                                            form={form}
                                                            reportIndex={index}
                                                            classificationData={classificationData}
                                                            disabled={disabled}
                                                        />
                                                    </Fragment>
                                                );
                                            })}
                                        </Accordion>
                                    )}
                                </form.Subscribe>
                            </div>
                        )}

                <Separator />

                <form.Field
                    name="locationRemark"
                    validators={{
                        onChange: validators.patchElectricalProjectObservationCommand.locationRemark,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label label={t('observations.form.location')}>
                            <Input
                                type="text"
                                name={name}
                                onChange={e => handleChange(e.target.value)}
                                value={state.value}
                                placeholder="Noteer locatie"
                                disabled={disabled}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>

                <form.Subscribe
                    selector={state => ({
                        fileId: state.values.floorplanElectricalProjectFileId,
                        pins: {
                            x: state.values.floorplanPinCoordinateX,
                            y: state.values.floorplanPinCoordinateY,
                        },
                    })}
                >
                    {({ fileId, pins }) => (
                        <Label label={t('observations.form.addPinLabel')} htmlFor="floorplan">
                            <PinField
                                listQuery={data?.Files?.filter(x => x.Kind === 'Floorplan') ?? []}
                                onFloorplanChange={handleFloorplanChange}
                                onPinChange={handlePinChange}
                                pins={pins}
                                value={fileId}
                                electricalProjectVersionId={electricalProjectVersionId}
                            />
                        </Label>
                    )}
                </form.Subscribe>
            </Popout.Body>
            <Popout.Footer>
                <Button
                    className="w-full"
                    variant="outline"
                    onClick={onClose}
                    disabled={disabled}
                >
                    {t('actions.general.cancel_short', { ns: 'common' })}
                </Button>
                <Button
                    className="w-full"
                    onClick={onSubmit}
                    disabled={disabled}
                >
                    {t('actions.general.save', { ns: 'common' })}
                </Button>
            </Popout.Footer>
        </Popout.Content>
    );
}
